import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import { useNavigate, useLocation } from 'react-router-dom';
import { useImpersonation } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { AspectGroupShelfSubgroup, ChannelItem, ChannelScope } from 'types/channel';
import { RatingDistribution } from 'types/marketplace';
import { useChannelDetail } from '../../../hooks/useChannelDetail';
import type { ChannelItemWithSyndicationSource } from '../../../hooks/useItemDetail';
import { useSubscription } from '../../../hooks/useSubscription';
import { useRatings } from '../../../hooks/useRatings';
import { useSubmitRating } from '../../../hooks/useSubmitRating';
import {
  getChannelItems,
  getMarketplaceMetrics,
  approveChannel,
  rejectChannel,
} from '../../../api/marketplaceApi';
import {
  SubscribeButtonState,
  deriveSubscribeButtonState,
} from '../../../components/atoms/marketplace/SubscribeButtonAtom';
import StatusBadgeAtom from '../../../components/atoms/StatusBadge/StatusBadgeAtom';
import ButtonAtom from '../../../components/atoms/Button/ButtonAtom';
import MessageAtom from '../../../components/atoms/Message/MessageAtom';
import ScopeCoinAtom from '../../../components/atoms/marketplace/ScopeCoinAtom';
import StarDisplayAtom from '../../../components/atoms/marketplace/StarDisplayAtom';
import UseCaseTagPillsAtom from '../../../components/atoms/marketplace/UseCaseTagPillsAtom';
import TagAtom from '../../../components/atoms/Tag/TagAtom';
import { MARKETPLACE_ITEM_TYPE_ICONS } from '../../../components/atoms/marketplace/iconMaps';
import { MarketplaceItemType } from '../../../components/atoms/marketplace/ItemTypeGlyphAtom';
import ItemCardMolecule from '../../../components/molecules/marketplace/ItemCardMolecule';
import ChannelShelfSectionMolecule from '../../../components/molecules/marketplace/ChannelShelfSectionMolecule';
import RatingSummaryMolecule from '../../../components/molecules/marketplace/RatingSummaryMolecule';
import RatingFormMolecule from '../../../components/molecules/marketplace/RatingFormMolecule';
import type { RatingFormSubmitData } from '../../../components/molecules/marketplace/RatingFormMolecule';
import ReviewListMolecule from '../../../components/molecules/marketplace/ReviewListMolecule';
import type { ReviewItem } from '../../../components/molecules/marketplace/ReviewListMolecule';
import SubscriptionsPanelOrganism from './SubscriptionsPanelOrganism';
import ChannelStatusCalloutMolecule, {
  ChannelReviewStatus,
} from '../../../components/molecules/marketplace/ChannelStatusCalloutMolecule';
import ManagerReviewBarMolecule from '../../../components/molecules/marketplace/ManagerReviewBarMolecule';
import { useMarketplacePermissions } from '../../../hooks/useMarketplacePermissions';
import { getLocalizedText } from '../../../utils/getLocalizedText';
import { PrimeReactSingleSelect } from '../../../design-system-my-lib';
import type { OptionType } from '../../atoms/single-select-dropdown/single-select-dropdown';
import './ChannelDetailOrganism.scss';

const REVIEWS_ANCHOR_ID = 'reviews';

export interface ChannelDetailOrganismProps {
  channelId: string;
  isSubscribed: boolean;
  isMandatory: boolean;
  onBack: () => void;
  onSubscriptionChange: () => void;
  onManage?: (channelId: string) => void;
  /** Sphere ID of the navbar-selected sphere — enables the Nearby tab in the picker. */
  nearbyAnchorSphereId?: string;
}

// Order chips by the canonical MarketplaceItemType union; surface_template is
// counted in the histogram but not chipped (no atom-level glyph for it yet).
const FACET_TYPES: MarketplaceItemType[] = [
  'approach',
  'video',
  'metric_library',
  'evaluation',
  'assistant',
  'form',
  'schema',
  'context',
  'arc_template',
  'orbit_template',
  'metaspace',
  'aspect',
  'aspect_group',
  'composition',
];

const FACET_LABEL_KEY: Record<MarketplaceItemType, string> = {
  approach: 'marketplace.channelDetail.typeFacet.approach',
  video: 'marketplace.channelDetail.typeFacet.video',
  metric_library: 'marketplace.channelDetail.typeFacet.metricLibrary',
  evaluation: 'marketplace.channelDetail.typeFacet.evaluation',
  assistant: 'marketplace.channelDetail.typeFacet.assistant',
  form: 'marketplace.channelDetail.typeFacet.form',
  schema: 'marketplace.channelDetail.typeFacet.schema',
  context: 'marketplace.channelDetail.typeFacet.context',
  arc_template: 'marketplace.channelDetail.typeFacet.arcTemplate',
  orbit_template: 'marketplace.channelDetail.typeFacet.orbitTemplate',
  metaspace: 'marketplace.channelDetail.typeFacet.metaspace',
  aspect: 'marketplace.channelDetail.typeFacet.aspect',
  aspect_group: 'marketplace.channelDetail.typeFacet.aspectGroup',
  composition: 'marketplace.channelDetail.typeFacet.composition',
};

// Item types that navigate to a detail view when clicked. `approach` uses its
// own dedicated route (ApproachDetailPageOrganism); the seven template-
// supertype types share the generic TemplateDetailPageOrganism route. Other
// item types (video/metric_library/evaluation/assistant/form/surface_template)
// have no detail view yet and remain non-navigable.
const NAVIGABLE_TEMPLATE_TYPES: ReadonlySet<string> = new Set([
  'schema',
  'context',
  'arc_template',
  'orbit_template',
  'metaspace',
  'aspect',
  'aspect_group',
  'composition',
]);

const SCOPE_KEYS: Record<ChannelScope, string> = {
  center: 'marketplace.common.scope.center',
  organization: 'marketplace.common.scope.organization',
  metasphere: 'marketplace.common.scope.metasphere',
};

const CHANNEL_STATUS_PILL: Record<
  ChannelReviewStatus,
  { tone: 'info' | 'warn' | 'danger' | 'success'; icon: string; labelKey: string }
> = {
  draft: {
    tone: 'info',
    icon: 'pi pi-file-edit',
    labelKey: 'marketplace.channels.status.callout.draft.label',
  },
  pending_review: {
    tone: 'warn',
    icon: 'pi pi-clock',
    labelKey: 'marketplace.channels.status.callout.pendingReview.label',
  },
  rejected: {
    tone: 'danger',
    icon: 'pi pi-times-circle',
    labelKey: 'marketplace.channels.status.callout.rejected.label',
  },
  approved: {
    tone: 'success',
    icon: 'pi pi-check-circle',
    labelKey: 'marketplace.channels.status.callout.approved.label',
  },
};

/**
 * Extract a display label for a syndicated item's source channel.
 * Uses the source channel `system_name` returned by the backend; until a name join
 * is wired, the system_name serves as a readable fallback.
 */
const getItemSyndicationSourceName = (item: ChannelItem): string | undefined => {
  if (!item.is_syndicated) return undefined;
  return (item as ChannelItemWithSyndicationSource).syndication_source?.source_channel_system_name;
};

export const ChannelDetailOrganism: React.FC<ChannelDetailOrganismProps> = ({
  channelId,
  isSubscribed,
  isMandatory,
  onBack,
  onSubscriptionChange,
  onManage,
  nearbyAnchorSphereId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const apiClients = useApiClientsOrNull();
  const { impersonationDetails } = useImpersonation();
  const organizationId = impersonationDetails?.organizationId;

  const { channel, loading, error, refetch } = useChannelDetail(channelId);
  const { toggleSubscription, isToggling } = useSubscription();
  const isOwner = channel?.owner_tenant_id === organizationId;

  // Marketplace manager check via publisher permissions (fail-closed when null).
  // For metasphere channels, fetches publisher-level permissions which include
  // the manage_marketplace predicate via canApprove.
  const publisherSlug = channel?.scope === 'metasphere' ? (channel?.publisher?.slug ?? null) : null;
  const {
    permissions: marketplacePermissions,
    unavailable: marketplacePermissionsUnavailable,
    refetch: refetchMarketplacePermissions,
  } = useMarketplacePermissions(publisherSlug);
  const isMarketplaceManager = marketplacePermissions?.canApprove ?? false;

  // Approve / reject state for manager review bar
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  // Flat items list (V2 grid is no longer grouped by use case).
  const [items, setItems] = useState<ChannelItem[]>([]);
  const [itemsTotal, setItemsTotal] = useState(0);
  const [itemsLoading, setItemsLoading] = useState(false);

  // Channel metrics
  const [metrics, setMetrics] = useState<{
    subscriberCount: number;
    importCount: number;
    ratingAverage: number;
    ratingCount: number;
    ratingDistribution: RatingDistribution;
  } | null>(null);

  // Rating form visibility
  const [showRatingForm, setShowRatingForm] = useState(false);

  // Item search, type filter, and sort
  const [itemSearch, setItemSearch] = useState('');
  const [itemTypeFilter, setItemTypeFilter] = useState<string>('all');
  const [itemSort, setItemSort] = useState<string>('default');

  // Fetch flat item list
  useEffect(() => {
    if (!apiClients || !organizationId || !channelId) {
      setItems([]);
      setItemsTotal(0);
      return;
    }

    let cancelled = false;
    setItemsLoading(true);

    getChannelItems(apiClients.schemaClient, channelId)
      .then((response) => {
        if (!cancelled) {
          setItems(response.data);
          setItemsTotal(response.total);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setItems([]);
          setItemsTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setItemsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apiClients, organizationId, channelId]);

  // Fetch channel metrics
  useEffect(() => {
    if (!apiClients || !channelId) {
      setMetrics(null);
      return;
    }

    let cancelled = false;

    getMarketplaceMetrics(apiClients.schemaClient, 'channel', channelId)
      .then((response) => {
        if (!cancelled) {
          setMetrics({
            subscriberCount: response.metrics.subscriber_count,
            importCount: response.metrics.import_count,
            ratingAverage: response.metrics.rating_average,
            ratingCount: response.metrics.rating_count,
            ratingDistribution: response.metrics.rating_distribution || {},
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setMetrics(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [apiClients, channelId]);

  // Ratings & Reviews
  const {
    ratings,
    total: ratingsTotal,
    userRating,
    loading: ratingsLoading,
    hasMore: ratingsHasMore,
    loadMore: loadMoreRatings,
    refetch: refetchRatings,
  } = useRatings('channel', channelId);

  const { submit: submitRatingFn, submitting } = useSubmitRating(
    undefined,
    undefined,
    undefined
  );

  // Filter items by search query and item type (must be before any early returns)
  const filteredItems = useMemo(() => {
    const hasSearch = itemSearch.trim().length > 0;
    const hasTypeFilter = itemTypeFilter !== 'all';
    if (!hasSearch && !hasTypeFilter) return items;
    const query = itemSearch.toLowerCase();
    return items.filter((item) => {
      if (hasTypeFilter) {
        const visualType =
          item.item_type === 'surface_template' ? 'evaluation' : item.item_type;
        if (visualType !== itemTypeFilter) return false;
      }
      if (hasSearch) {
        const name = getLocalizedText(item.item_metadata?.name) || '';
        const desc = getLocalizedText(item.item_metadata?.description) || '';
        if (!name.toLowerCase().includes(query) && !desc.toLowerCase().includes(query)) {
          return false;
        }
      }
      return true;
    });
  }, [items, itemSearch, itemTypeFilter]);

  const channelSortOptions = useMemo<OptionType[]>(
    () => [
      { name: t('marketplace.channelDetail.items.sort.default'), code: 'default' },
      { name: t('marketplace.channelDetail.items.sort.name'), code: 'name' },
      { name: t('marketplace.channelDetail.items.sort.rating'), code: 'rating' },
      { name: t('marketplace.channelDetail.items.sort.newest'), code: 'newest' },
    ],
    [t]
  );

  const selectedSortOption = useMemo<OptionType>(
    () => channelSortOptions.find((o) => o.code === itemSort) ?? channelSortOptions[0],
    [channelSortOptions, itemSort]
  );

  const sortedItems = useMemo(() => {
    if (itemSort === 'default') return filteredItems;
    return [...filteredItems].sort((a, b) => {
      if (itemSort === 'name') {
        const nameA = getLocalizedText(a.item_metadata?.name) ?? '';
        const nameB = getLocalizedText(b.item_metadata?.name) ?? '';
        return nameA.localeCompare(nameB);
      }
      if (itemSort === 'rating') {
        const ratingA = (a.item_metadata as Record<string, unknown>)?.['average_rating'] as number ?? 0;
        const ratingB = (b.item_metadata as Record<string, unknown>)?.['average_rating'] as number ?? 0;
        return ratingB - ratingA;
      }
      if (itemSort === 'newest') {
        const versionA = a.item_version ?? 0;
        const versionB = b.item_version ?? 0;
        return versionB - versionA;
      }
      return 0;
    });
  }, [filteredItems, itemSort]);

  // Histogram of item counts per type, plus a totalCount across all items.
  // surface_template is counted under its visual analog 'evaluation' so it
  // remains filterable; matches the mapping ItemCardMolecule uses to render
  // the glyph/chip atoms.
  const { typeHistogram, totalItemCount } = useMemo(() => {
    const histogram: Partial<Record<MarketplaceItemType, number>> = {};
    for (const item of items) {
      const t: MarketplaceItemType =
        item.item_type === 'surface_template'
          ? 'evaluation'
          : (item.item_type as MarketplaceItemType);
      histogram[t] = (histogram[t] ?? 0) + 1;
    }
    return { typeHistogram: histogram, totalItemCount: items.length };
  }, [items]);

  // Distinct types present (used to decide whether to render the facet bar).
  const distinctFacetTypes = useMemo(
    () => FACET_TYPES.filter((t) => (typeHistogram[t] ?? 0) > 0),
    [typeHistogram]
  );

  // Aspect items by system_name, for resolving a shelf's server-sent membership
  // (system_names only) back to the full ChannelItem the card needs to render.
  // Built from `items`, NOT `filteredItems`: this is a resolution index, not a display list.
  // The facet/search decides which shelves and which flat cards appear; a displayed shelf then
  // shows its true curated membership. Sourcing it from `filteredItems` starved resolution —
  // selecting the `aspect_group` facet left the map empty, so every shelf resolved 0 members and
  // self-omitted, while `unclaimedFilteredItems` was empty too (it excludes `aspect_group`),
  // rendering a completely blank content area with no empty state.
  const aspectItemsBySystemName = useMemo(() => {
    const map = new Map<string, ChannelItem>();
    for (const item of items) {
      if (item.item_type === 'aspect') map.set(item.item_system_name, item);
    }
    return map;
  }, [items]);

  const resolveAspectItems = useCallback(
    (systemNames: string[]): ChannelItem[] =>
      systemNames
        .map((name) => aspectItemsBySystemName.get(name))
        .filter((item): item is ChannelItem => Boolean(item)),
    [aspectItemsBySystemName]
  );

  // Published aspect_group items whose live shelf was resolved server-side (EAINT-864
  // Phase 3). Every aspect claimed by at least one shelf (at any nesting depth) is
  // excluded from the residual flat grid below — it may still appear under MULTIPLE
  // shelves (many-to-many is the point), just never duplicated into the flat grid too.
  const shelfItems = useMemo(
    () => filteredItems.filter((item) => item.item_type === 'aspect_group' && item.aspect_group_shelf),
    [filteredItems]
  );

  // Only names that actually RESOLVE to a rendered card count as claimed. The server
  // resolves shelf membership unpaginated (a shelf must reflect every published aspect,
  // not just the current page), so a claimed name may name an aspect that is not in
  // `filteredItems` — off the current page, or filtered out. Claiming it anyway would
  // remove it from the flat grid while the shelf cannot render it either, and the item
  // would vanish from the page entirely.
  const claimedAspectSystemNames = useMemo(() => {
    const claimed = new Set<string>();
    const collect = (shelf: { aspects: string[]; subgroups: AspectGroupShelfSubgroup[] }) => {
      for (const name of shelf.aspects) {
        if (aspectItemsBySystemName.has(name)) claimed.add(name);
      }
      for (const subgroup of shelf.subgroups) collect(subgroup);
    };
    for (const item of shelfItems) {
      if (item.aspect_group_shelf) collect(item.aspect_group_shelf);
    }
    return claimed;
  }, [shelfItems, aspectItemsBySystemName]);

  // Residual flat grid, in the chosen sort order: everything except the shelf headers
  // themselves (aspect_group items render as shelves, never as cards) and any aspect
  // claimed by a shelf.
  const unclaimedFilteredItems = useMemo(
    () =>
      sortedItems.filter(
        (item) =>
          item.item_type !== 'aspect_group' &&
          !(item.item_type === 'aspect' && claimedAspectSystemNames.has(item.item_system_name))
      ),
    [sortedItems, claimedAspectSystemNames]
  );


  // For owners, always show subscription state (not 'manage') since
  // the separate Manage Channel button handles that. This avoids
  // showing two manage buttons and lets owners subscribe their org too.
  const subscribeState: SubscribeButtonState | null = channel
    ? deriveSubscribeButtonState({
        isOwner: false,
        status: channel.status,
        isSubscribed,
        isMandatory,
      })
    : null;

  const handleSubscribeClick = useCallback(() => {
    toggleSubscription(channelId, isSubscribed).then((success) => {
      if (success) {
        onSubscriptionChange();
      }
    });
  }, [channelId, isSubscribed, toggleSubscription, onSubscriptionChange]);

  const handleApproveChannel = useCallback(async () => {
    if (!apiClients?.schemaClient) return;
    setApproveLoading(true);
    setReviewError(null);
    try {
      await approveChannel(apiClients.schemaClient, channelId);
      refetch();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('marketplace.channelDetail.review.approveFailed');
      setReviewError(message);
    } finally {
      setApproveLoading(false);
    }
  }, [apiClients, channelId, refetch, t]);

  const handleRejectChannel = useCallback(
    async (reason: string) => {
      if (!apiClients?.schemaClient) return;
      setRejectLoading(true);
      setReviewError(null);
      try {
        await rejectChannel(apiClients.schemaClient, channelId, reason);
        refetch();
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : t('marketplace.channelDetail.review.rejectFailed');
        setReviewError(message);
      } finally {
        setRejectLoading(false);
      }
    },
    [apiClients, channelId, refetch, t],
  );

  const handleItemClick = useCallback(
    (item: ChannelItem) => {
      if (item.item_type === 'approach') {
        const systemName = item.item_system_name || item.item_metadata?.system_name;
        if (systemName) {
          navigate(`/marketplace/approaches/${encodeURIComponent(systemName)}`, {
            state: { from: location.pathname, channelId: channel?.id, channelName: getLocalizedText(channel?.name) },
          });
        }
        return;
      }
      if (NAVIGABLE_TEMPLATE_TYPES.has(item.item_type)) {
        navigate(`/marketplace/channels/${encodeURIComponent(channelId)}/items/${encodeURIComponent(item.id)}`, {
          state: { from: location.pathname, channelId: channel?.id, channelName: getLocalizedText(channel?.name) },
        });
      }
    },
    [navigate, location.pathname, channel?.id, channel?.name, channelId]
  );

  const isItemClickable = useCallback(
    (item: ChannelItem) => item.item_type === 'approach' || NAVIGABLE_TEMPLATE_TYPES.has(item.item_type),
    []
  );

  const handleViewGroup = useCallback(
    (systemName: string) => {
      const groupItem = shelfItems.find((item) => item.item_system_name === systemName);
      if (groupItem) handleItemClick(groupItem);
    },
    [shelfItems, handleItemClick]
  );

  const handleRatingSubmit = useCallback(
    async (data: RatingFormSubmitData) => {
      const result = await submitRatingFn({
        targetType: 'channel',
        targetId: channelId,
        stars: data.stars,
        review: data.reviewBody
          ? { title: data.reviewTitle || null, body: data.reviewBody }
          : undefined,
      });
      if (result) {
        setShowRatingForm(false);
        refetchRatings();
        // Re-fetch metrics to update rating average
        if (apiClients) {
          getMarketplaceMetrics(apiClients.schemaClient, 'channel', channelId)
            .then((response) => {
              setMetrics({
                subscriberCount: response.metrics.subscriber_count,
                importCount: response.metrics.import_count,
                ratingAverage: response.metrics.rating_average,
                ratingCount: response.metrics.rating_count,
                ratingDistribution: response.metrics.rating_distribution || {},
              });
            })
            .catch(() => { /* non-fatal */ });
        }
      }
    },
    [channelId, submitRatingFn, refetchRatings, apiClients]
  );

  // Map ratings to ReviewItem format
  const reviewItems: ReviewItem[] = useMemo(() => {
    return ratings
      .filter((r) => r.review)
      .map((r) => ({
        id: r.id,
        stars: r.stars,
        title: r.review?.title,
        body: r.review?.body || '',
        // Discover-safe author resolved server-side; null → card shows "Anonymous".
        // Never fall back to the raw user_id (that leaked the identity UUID).
        authorName: r.author?.displayName ?? null,
        author: r.author ?? null,
        date: r.created_at,
        isEdited: r.review?.is_edited,
      }));
  }, [ratings]);

  const displayName = getLocalizedText(channel?.name);
  const displayDescription = getLocalizedText(channel?.description);
  const useCaseTags = channel?.use_case_tags || [];

  // Dynamic page title. Capture the title at mount and restore it on unmount
  // so concurrently-rendered organisms don't clobber each other's titles.
  useEffect(() => {
    if (!displayName) return;
    const previous = document.title;
    document.title = `${displayName} - ${t('marketplace.pageTitle')} - ${t('header.title')}`;
    return () => {
      document.title = previous;
    };
  }, [displayName, t]);

  if (loading && !channel) {
    return (
      <div className="marketplace-channel-detail">
        <button
          type="button"
          className="marketplace-channel-detail__back"
          onClick={onBack}
        >
          <i className="pi pi-arrow-left" aria-hidden="true" />
          <span>{t('marketplace.channels.detail.backToMarketplace')}</span>
        </button>
        <div className="marketplace-channel-detail__loading">
          <span className="marketplace-marketplace__spinner" />
          <p>{t('marketplace.channels.detail.loadingDetails')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="marketplace-channel-detail">
        <button
          type="button"
          className="marketplace-channel-detail__back"
          onClick={onBack}
        >
          <i className="pi pi-arrow-left" aria-hidden="true" />
          <span>{t('marketplace.channels.detail.backToMarketplace')}</span>
        </button>
        <div className="marketplace-channel-detail__error">
          <p className="marketplace-marketplace__error-message">{error}</p>
          <button
            type="button"
            className="marketplace-marketplace__retry-btn"
            onClick={refetch}
          >
            {t('marketplace.common.retry')}
          </button>
        </div>
      </div>
    );
  }

  if (!channel) {
    return null;
  }

  const reviewableStatus = channel.status as ChannelReviewStatus;
  const isReviewableStatus =
    channel.status === 'draft' ||
    channel.status === 'pending_review' ||
    channel.status === 'rejected';
  const statusPillMeta =
    isOwner && isReviewableStatus ? CHANNEL_STATUS_PILL[reviewableStatus] : null;

  return (
    <div className="marketplace-channel-detail">
      {/* Manager review bar — shown at top for marketplace managers when channel is pending */}
      {isMarketplaceManager && channel.status === 'pending_review' && (
        <ManagerReviewBarMolecule
          permissions={marketplacePermissions}
          publisherStatus="pending_review"
          entityKind="channel"
          onApprove={handleApproveChannel}
          onReject={handleRejectChannel}
          approveLoading={approveLoading}
          rejectLoading={rejectLoading}
          className="marketplace-channel-detail__manager-review-bar"
        />
      )}
      {marketplacePermissionsUnavailable && channel.status === 'pending_review' && (
        <div
          className="marketplace-channel-detail__permissions-unavailable"
          data-testid="channel-review-permissions-unavailable"
        >
          <MessageAtom severity="warn" text={t('marketplace.channels.detail.reviewPermissionsUnavailable')} />
          <ButtonAtom
            type="button"
            variant="text"
            size="small"
            label={t('common.retry')}
            onClick={refetchMarketplacePermissions}
          />
        </div>
      )}
      {reviewError && (
        <div
          role="alert"
          className="marketplace-marketplace__error-message marketplace-channel-detail__review-error"
        >
          <i className="pi pi-exclamation-triangle" aria-hidden="true" />
          <span>{reviewError}</span>
          <button
            type="button"
            className="marketplace-channel-detail__review-error-dismiss"
            onClick={() => setReviewError(null)}
            aria-label={t('marketplace.common.dismiss')}
          >
            ×
          </button>
        </div>
      )}

      <button
        type="button"
        className="marketplace-channel-detail__back"
        onClick={onBack}
      >
        <i className="pi pi-arrow-left" aria-hidden="true" />
        <span>{t('marketplace.channels.detail.backToMarketplace')}</span>
      </button>

      <div
        className={`marketplace-channel-detail__header marketplace-channel-detail__header--scope-${channel.scope}`}
      >
        <div className="marketplace-channel-detail__header-info">
          <ScopeCoinAtom
            scope={channel.scope}
            glyph={channel.glyph ?? null}
            glyphKind={channel.glyph_kind}
            channelName={displayName}
            hue={channel.hue}
            size="lg"
            isSystem={channel.is_system}
            className="marketplace-channel-detail__glyph"
          />
          <div className="marketplace-channel-detail__info-col">
            <div className="marketplace-channel-detail__title-row">
              <h1 className="marketplace-channel-detail__name">{displayName}</h1>
              {statusPillMeta && (
                <StatusBadgeAtom
                  tone={statusPillMeta.tone}
                  icon={statusPillMeta.icon}
                  label={t(statusPillMeta.labelKey)}
                  size="small"
                  className="marketplace-channel-detail__status-pill"
                />
              )}
              {isOwner && onManage && (
                <button
                  type="button"
                  className="marketplace-channel-detail__manage-btn"
                  onClick={() => onManage(channelId)}
                >
                  {t('marketplace.channels.detail.manageChannel')}
                </button>
              )}
            </div>
          <div className="marketplace-channel-detail__meta">
            <span
              className={`marketplace-channel-detail__scope-chip marketplace-channel-detail__scope-chip--${channel.scope}`}
            >
              <i
                className={`pi ${
                  channel.scope === 'metasphere'
                    ? 'pi-globe'
                    : channel.scope === 'organization'
                      ? 'pi-building'
                      : 'pi-compass'
                } marketplace-channel-detail__scope-chip-icon`}
                aria-hidden="true"
              />
              {t(SCOPE_KEYS[channel.scope])}
            </span>
            {channel.scope === 'metasphere' && channel.publisher && (
              <>
                <span className="marketplace-channel-detail__separator">&middot;</span>
                <button
                  type="button"
                  className="marketplace-channel-detail__publisher-link"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(
                      `/marketplace/publishers/${encodeURIComponent(channel.publisher!.slug)}`,
                    );
                  }}
                >
                  <span className="marketplace-channel-detail__publisher-by">
                    by
                  </span>
                  <span className="marketplace-channel-detail__publisher-name">
                    {channel.publisher.display_name}
                  </span>
                  {channel.publisher.is_official && (
                    <i
                      className="pi pi-verified marketplace-channel-detail__publisher-verified"
                      aria-hidden="true"
                    />
                  )}
                </button>
              </>
            )}
            <span className="marketplace-channel-detail__separator">&middot;</span>
            <span className="marketplace-channel-detail__meta-stat">
              <i
                className="pi pi-users marketplace-channel-detail__meta-stat-icon"
                aria-hidden="true"
              />
              {t('marketplace.channels.detail.subscriberCount', {
                count: metrics?.subscriberCount ?? 0,
              })}
            </span>
            <span className="marketplace-channel-detail__separator">&middot;</span>
            <span className="marketplace-channel-detail__meta-stat">
              <i
                className="pi pi-th-large marketplace-channel-detail__meta-stat-icon"
                aria-hidden="true"
              />
              {t('marketplace.common.itemCount', { count: itemsTotal })}
            </span>
            <span className="marketplace-channel-detail__separator">&middot;</span>
            <a
              href={`#${REVIEWS_ANCHOR_ID}`}
              className="marketplace-channel-detail__rating-chip"
              aria-label={
                metrics && metrics.ratingCount > 0
                  ? t(
                      'marketplace.channelDetail.header.ratingChipAriaLabel',
                      {
                        rating: metrics.ratingAverage.toFixed(1),
                        count: metrics.ratingCount,
                      },
                    )
                  : t('marketplace.ratings.noRatings')
              }
            >
              <StarDisplayAtom
                rating={metrics?.ratingAverage ?? 0}
                size="small"
              />
              {metrics && metrics.ratingCount > 0 && (
                <span className="marketplace-channel-detail__rating-chip-count">
                  ({metrics.ratingCount})
                </span>
              )}
            </a>
          </div>
          {displayDescription && (
            <p className="marketplace-channel-detail__description">
              {displayDescription}
            </p>
          )}
          {useCaseTags.length > 0 && (
            <UseCaseTagPillsAtom
              tags={useCaseTags}
              size="small"
              className="marketplace-channel-detail__tags"
            />
          )}
          </div>
        </div>
        <div className="marketplace-channel-detail__header-actions">
          {/* Unified Subscriptions card — personal "You" + org-sphere subscriptions. */}
          {organizationId && (
            <SubscriptionsPanelOrganism
              channelId={channelId}
              organizationId={organizationId}
              isOwner={isOwner}
              subscribeState={subscribeState}
              isTogglingSubscription={isToggling(channelId)}
              onPersonalSubscribeClick={handleSubscribeClick}
              nearbyAnchorSphereId={nearbyAnchorSphereId}
            />
          )}
        </div>
      </div>

      {/* Status callout — shown to owners for draft / pending_review / rejected */}
      {isOwner && isReviewableStatus && (
        <ChannelStatusCalloutMolecule
          status={reviewableStatus}
          reviewerNote={channel.reviewer_note}
          onResubmit={
            channel.status === 'rejected' && onManage
              ? () => onManage(channelId)
              : undefined
          }
          className="marketplace-channel-detail__status-callout"
        />
      )}

      {/* Search bar + sort dropdown row */}
      {itemsTotal > 0 && (
        <div className="marketplace-channel-detail__search-row">
          <div className="marketplace-channel-detail__filter-bar">
            <i
              className="pi pi-search marketplace-channel-detail__search-icon"
              aria-hidden="true"
            />
            <input
              type="text"
              className="marketplace-channel-detail__search"
              placeholder={t(
                'marketplace.channelDetail.items.searchPlaceholderInChannel',
                { count: itemsTotal, channel: displayName },
              )}
              aria-label={t('marketplace.channelDetail.items.searchAriaLabel')}
              value={itemSearch}
              onChange={(e) => setItemSearch(e.target.value)}
            />
          </div>
          <div className="marketplace-channel-detail__sort-wrapper">
            <PrimeReactSingleSelect
              value={selectedSortOption}
              options={channelSortOptions}
              onChange={(e) => setItemSort(e.value?.code ?? 'default')}
              className="marketplace-channel-detail__sort-dropdown sort-by-dropdown"
              aria-label={t('marketplace.channelDetail.items.sort.ariaLabel')}
            />
          </div>
        </div>
      )}

      {/* Type facet bar — always show when the channel has items, so the
          design's filter-chip row ("All N", "Approaches N", …) appears even
          for single-type channels. */}
      {itemsTotal > 0 && (
        <div
          className="marketplace-channel-detail__type-facet-bar"
          role="group"
          aria-label={t('marketplace.channelDetail.typeFacet.ariaLabel')}
        >
          <TagAtom
            tone="neutral"
            icon="pi-th-large"
            label={t('marketplace.channelDetail.typeFacet.all')}
            count={totalItemCount}
            pressed={itemTypeFilter === 'all'}
            size="small"
            onClick={() => setItemTypeFilter('all')}
            data-testid="type-facet-chip-all"
          />
          {distinctFacetTypes.map((type) => (
            <TagAtom
              key={type}
              tone="neutral"
              icon={MARKETPLACE_ITEM_TYPE_ICONS[type]}
              label={t(FACET_LABEL_KEY[type])}
              count={typeHistogram[type] ?? 0}
              pressed={itemTypeFilter === type}
              size="small"
              onClick={() =>
                setItemTypeFilter(itemTypeFilter === type ? 'all' : type)
              }
              data-testid={`type-facet-chip-${type}`}
            />
          ))}
        </div>
      )}

      {/* Aspect group shelves — each published group renders its live membership as a
          named section above the flat grid; a subgroup renders as a nested sub-section. */}
      {shelfItems.map((item) => (
        <ChannelShelfSectionMolecule
          key={item.id}
          systemName={item.item_system_name}
          name={item.item_metadata?.name ?? { default: item.item_system_name }}
          intent={item.aspect_group_shelf?.intent ?? null}
          aspectItems={resolveAspectItems(item.aspect_group_shelf?.aspects ?? [])}
          subgroups={item.aspect_group_shelf?.subgroups ?? []}
          resolveAspectItems={resolveAspectItems}
          onItemClick={handleItemClick}
          onHeaderClick={handleViewGroup}
          getItemSyndicationSourceName={getItemSyndicationSourceName}
        />
      ))}

      {/* Flat items grid */}
      <div className="marketplace-channel-detail__items">
        {(() => {
          if (itemsLoading && items.length === 0) {
            return (
              <div className="marketplace-channel-detail__loading">
                <span className="marketplace-marketplace__spinner" />
                <p>{t('marketplace.common.loading')}</p>
              </div>
            );
          }
          if (itemsTotal === 0) {
            return (
              <div className="marketplace-channel-detail__empty-state">
                <i
                  className="pi pi-box marketplace-channel-detail__empty-state-icon"
                  aria-hidden="true"
                />
                <p className="marketplace-channel-detail__empty-state-title">
                  {t('marketplace.channelDetail.items.emptyChannel')}
                </p>
                <p className="marketplace-channel-detail__empty-state-helper">
                  {t('marketplace.channelDetail.items.emptyChannelHint')}
                </p>
              </div>
            );
          }
          if (sortedItems.length === 0) {
            return (
              <div className="marketplace-channel-detail__empty">
                <p>{t('marketplace.channelDetail.items.noSearchResults')}</p>
              </div>
            );
          }
          if (unclaimedFilteredItems.length === 0) {
            // Only silent when shelves are carrying the content above this grid. Without the
            // guard, a filter that leaves nothing BUT unrenderable items (e.g. `aspect_group`
            // rows whose shelf enrichment is absent) blanks the region with no empty state.
            return shelfItems.length > 0 ? null : (
              <div className="marketplace-channel-detail__empty">
                <p>{t('marketplace.channelDetail.items.noSearchResults')}</p>
              </div>
            );
          }
          return (
            <div className="marketplace-channel-detail__items-grid">
              {unclaimedFilteredItems.map((item) => (
                <ItemCardMolecule
                  key={item.id}
                  item={item}
                  onClick={isItemClickable(item) ? handleItemClick : undefined}
                  sourceChannelName={getItemSyndicationSourceName(item)}
                />
              ))}
            </div>
          );
        })()}
      </div>

      {/* Ratings & Reviews section */}
      <div id={REVIEWS_ANCHOR_ID} className="marketplace-channel-detail__ratings-section">
        <div className="marketplace-channel-detail__ratings-header">
          <h3 className="marketplace-channel-detail__section-title">
            {t('marketplace.ratings.title')}
          </h3>
          {!showRatingForm && !userRating && (
            <button
              type="button"
              className="marketplace-channel-detail__rate-btn"
              onClick={() => setShowRatingForm(true)}
            >
              {t('marketplace.channels.detail.rateThisChannel')}
            </button>
          )}
        </div>

        <div className="marketplace-channel-detail__ratings-content">
          <RatingSummaryMolecule
            ratingAverage={metrics?.ratingAverage || 0}
            ratingCount={metrics?.ratingCount || 0}
            distribution={metrics?.ratingDistribution || {}}
          />

          {showRatingForm && (
            <RatingFormMolecule
              initialStars={userRating?.stars}
              initialTitle={userRating?.review?.title || ''}
              initialBody={userRating?.review?.body || ''}
              onSubmit={handleRatingSubmit}
              onCancel={() => setShowRatingForm(false)}
              isSubmitting={submitting}
              isUpdate={!!userRating}
              className="marketplace-channel-detail__rating-form"
            />
          )}
          {!showRatingForm && userRating && (
            <div className="marketplace-channel-detail__user-rating">
              <span className="marketplace-channel-detail__user-rating-label">
                {t('marketplace.ratings.yourRating')}
              </span>
              <button
                type="button"
                className="marketplace-channel-detail__edit-rating-btn"
                onClick={() => setShowRatingForm(true)}
              >
                {t('marketplace.ratings.editRating')}
              </button>
            </div>
          )}
        </div>

      </div>

      {/* Recent reviews subheader + list, separate card per design. */}
      {!ratingsLoading && (
        <div className="marketplace-channel-detail__recent-reviews">
          <h3 className="marketplace-channel-detail__recent-reviews-title">
            {t('marketplace.ratings.recentReviewsTitle')}
          </h3>
          {reviewItems.length > 0 ? (
            <ReviewListMolecule
              reviews={reviewItems}
              totalCount={ratingsTotal}
              className="marketplace-channel-detail__reviews"
            />
          ) : (
            <p className="marketplace-channel-detail__recent-reviews-empty">
              {t('marketplace.ratings.noReviewsYet')}
            </p>
          )}
          {ratingsHasMore && (
            <button
              type="button"
              className="marketplace-channel-detail__load-more-btn"
              onClick={loadMoreRatings}
            >
              {t('marketplace.common.loadMore')}
            </button>
          )}
        </div>
      )}

    </div>
  );
};

export default ChannelDetailOrganism;
